/**
 * Keeps a screen from asking the same question several times at once.
 *
 * The app fans out on entering a tab, and Ionic keeps pages mounted, so the same
 * list is often requested again a moment after it arrived. On 28 August that cost
 * a member the whole minute's rate-limit allowance — `stories/tray` alone was
 * fetched 48 times in one second — and once the allowance was gone every other
 * call failed with it: the menu, the feed and the percorsi at the same moment,
 * because they share one bucket.
 *
 * Two protections, deliberately separate, because they are not equally safe:
 *
 *   `run`      joins a caller to a request already in flight for the same key.
 *              Always safe — the answer it hands back is the answer it would
 *              have fetched anyway.
 *
 *   `isFresh`  additionally refuses to ask again for a short while after a
 *              success. Only safe where a slightly stale answer is acceptable —
 *              a screen being re-entered, never a pagination step, which must
 *              always reach the network or the next page never loads.
 *
 * Everything is keyed, so a different question — another category, another date
 * range — is never mistaken for a repeat of the last one. The pattern is the one
 * already proven in courseActions.js for getCourse/getPills, which now uses this.
 */
export function createRequestDedupe({ throttleMs = 0, keyOf } = {}) {
  const inflight = new Map();
  const lastSuccess = new Map();

  const resolveKey = (arg) => {
    if (typeof keyOf !== "function") return "shared";
    const key = keyOf(arg);
    return key === undefined || key === null ? "shared" : String(key);
  };

  /** Run `fn`, or join the identical request already running. */
  const run = (arg, fn) => {
    const key = resolveKey(arg);
    const existing = inflight.get(key);
    if (existing) return existing;

    const promise = (async () => {
      try {
        const result = await fn();
        lastSuccess.set(key, Date.now());
        return result;
      } finally {
        // Cleared even when the request threw: a failure must never wedge the
        // key shut, or the screen could never retry.
        inflight.delete(key);
      }
    })();

    inflight.set(key, promise);
    return promise;
  };

  /** True when this exact question was answered recently enough to reuse. */
  const isFresh = (arg) => {
    if (!throttleMs) return false;
    const at = lastSuccess.get(resolveKey(arg));
    return at !== undefined && Date.now() - at < throttleMs;
  };

  /**
   * Forget what we know. Call after something that changes the answer — publishing
   * a Storia, completing a pillola — so the next read is not served a stale skip.
   */
  const reset = () => {
    inflight.clear();
    lastSuccess.clear();
  };

  return { run, isFresh, reset };
}

/** The window used for screen-entry reads: long enough to swallow a double mount. */
export const SCREEN_ENTRY_THROTTLE_MS = 5_000;
